/**
 * 游戏数据加载器工具
 * 提供游戏数据的异步和同步访问API
 */

// 游戏数据缓存
const gameCache = new Map();
// 游戏列表缓存
let gameSlugsCache = null;
// 游戏类别缓存
let genresCache = null;
// 游戏标签缓存
let tagsCache = null;
// 所有游戏数据缓存（用于同步API）
let allGamesCache = null;
// 缓存状态回调函数数组
const cacheStatusCallbacks = [];

/**
 * 动态加载游戏数据
 * @param {string} slug - 游戏slug
 * @returns {Promise<Object>} - 返回游戏数据对象
 */
export async function loadGameData(slug) {
  try {
    // 如果已经缓存，直接返回
    if (gameCache.has(slug)) {
      return gameCache.get(slug);
    }

    // 动态导入游戏数据
    const gameModule = await import(`./data/${slug}.js`);
    const gameData = gameModule.default;
    
    // 缓存游戏数据
    gameCache.set(slug, gameData);
    
    return gameData;
  } catch (error) {
    return null;
  }
}

/**
 * 获取所有游戏的slug列表
 * @returns {Promise<string[]>} - 返回所有游戏的slug列表
 */
export async function getAllGameSlugs() {
  try {
    // 如果已经缓存，直接返回
    if (gameSlugsCache) {
      return gameSlugsCache;
    }

    // 使用 import.meta.glob 动态获取所有游戏文件
    const gameFiles = import.meta.glob('../games/data/*.js', { eager: true });
    
    // 过滤出游戏文件并提取slug
    const slugs = Object.keys(gameFiles)
      .map(file => {
        const match = file.match(/\/data\/(.+)\.js$/);
        return match ? match[1] : null;
      })
      .filter(Boolean); // 过滤掉无效的slug
    
    // 缓存结果
    gameSlugsCache = slugs;
    
    return slugs;
  } catch (error) {
    return [];
  }
}

/**
 * 获取游戏的本地化数据
 * @param {Object} game - 游戏对象
 * @param {string} lang - 语言代码
 * @returns {Object} - 返回本地化后的游戏数据
 */
function getLocalizedGameData(game, lang = 'en') {
  if (!game || !game.i18n) return game;
  
  const i18nData = game.i18n[lang] || game.i18n.en;
  const enData = game.i18n.en;
  
  // 获取当前语言的类别和标签，如果没有则使用英文的
  const localizedGenres = (i18nData.genres && i18nData.genres.length > 0) ? i18nData.genres : enData.genres;
  const localizedTags = (i18nData.tags && i18nData.tags.length > 0) ? i18nData.tags : enData.tags;
  
  // 使用英文作为类别ID
  const genreIds = enData.genres || [];
  
  return {
    ...game,
    title: i18nData.title || enData.title,
    description: i18nData.description || enData.description,
    instructions: i18nData.instructions || enData.instructions,
    // 将类别和标签保持为ID形式，以便图标显示
    genres: genreIds,
    // 添加本地化类别名称，以便显示
    genreNames: localizedGenres,
    tags: localizedTags,
    // 添加一个方便的函数方法来获取本地化的类别名称
    getLocalizedGenre(index) {
      return (localizedGenres && localizedGenres[index]) || genreIds[index] || '';
    }
  };
}

/**
 * 分页获取游戏列表
 * @param {number} page - 页码
 * @param {number} pageSize - 每页数量
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<{games: Array, total: number, currentPage: number, totalPages: number}>}
 */
export async function getGamesByPage(page = 1, pageSize = 20, lang = 'en') {
  const slugs = await getAllGameSlugs();
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const pageSlugs = slugs.slice(start, end);
  
  const games = await Promise.all(
    pageSlugs.map(slug => loadGameData(slug))
  );

  return {
    games: games
      .filter(game => game !== null)
      .map(game => getLocalizedGameData(game, lang)),
    total: slugs.length,
    currentPage: page,
    totalPages: Math.ceil(slugs.length / pageSize)
  };
}

/**
 * 获取所有游戏类别
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<Array>} - 返回不重复的游戏类别列表，每个类别包含id属性
 */
export async function getAllGenres(lang = 'en') {
  if (genresCache) {
    return genresCache;
  }

  const slugs = await getAllGameSlugs();

  const games = await Promise.all(slugs.map(slug => loadGameData(slug)));
  
  // 用于存储类别ID的集合
  const genreIds = new Set();
  
  games.forEach(game => {
    if (game && game.i18n && game.i18n.en && game.i18n.en.genres) {
      // 使用英文类别作为ID
      game.i18n.en.genres.forEach(genre => genreIds.add(genre));
    }
  });

  // 如果没有找到任何类别，尝试使用硬编码的默认类别列表
  if (genreIds.size === 0) {
    const defaultGenres = [
      'Casual', 'Puzzle', 'Racing & Driving', 'Shooter', 
      'Simulation', 'Strategy', 'Sports', 'Agility', 
      'Battle', 'Care', 'Cooking', 'Football', '.IO', 'Merge'
    ];
    defaultGenres.forEach(genre => genreIds.add(genre));
  }

  // 转换为数组格式 [{id: 英文类别}]
  genresCache = Array.from(genreIds).map(id => ({ id }));
  return genresCache;
}

/**
 * 获取所有游戏标签
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<string[]>} - 返回不重复的游戏标签列表
 */
export async function getAllTags(lang = 'en') {
  if (tagsCache) {
    return tagsCache;
  }

  const slugs = await getAllGameSlugs();
  const games = await Promise.all(slugs.map(slug => loadGameData(slug)));
  
  const tags = new Set();
  games.forEach(game => {
    if (game && game.i18n && game.i18n[lang]) {
      game.i18n[lang].tags.forEach(tag => tags.add(tag));
    }
  });

  tagsCache = Array.from(tags);
  return tagsCache;
}

/**
 * 根据类别获取游戏
 * @param {string} genre - 游戏类别ID
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<Array>} - 返回指定类别的游戏列表
 */
export async function getGamesByGenre(genre, lang = 'en') {
  const slugs = await getAllGameSlugs();
  const games = await Promise.all(slugs.map(slug => loadGameData(slug)));
  
  return games
    .filter(game => {
      if (!game) return false;
      const i18nData = game.i18n[lang] || game.i18n.en;
      
      // 获取游戏的类别，如果当前语言有，则用当前语言的，否则用英文的
      const gameGenres = (i18nData.genres && i18nData.genres.length > 0) 
        ? i18nData.genres 
        : game.i18n.en.genres;
      
      // 获取英文类别，用于匹配
      const enGenres = game.i18n.en.genres || [];

      // 特殊类别处理
      if (genre === 'other') {
        return !gameGenres || gameGenres.length === 0;
      }
      
      // 首先尝试在当前语言类别中匹配
      if (gameGenres && gameGenres.includes(genre)) {
        return true;
      }
      
      // 如果在英文类别列表中找到，也认为匹配
      if (lang !== 'en' && enGenres && enGenres.includes(genre)) {
        return true;
      }
      
      return false;
    })
    .map(game => getLocalizedGameData(game, lang));
}

/**
 * 根据标签获取游戏
 * @param {string} tag - 游戏标签
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<Array>} - 返回指定标签的游戏列表
 */
export async function getGamesByTag(tag, lang = 'en') {
  const slugs = await getAllGameSlugs();
  const games = await Promise.all(slugs.map(slug => loadGameData(slug)));
  
  return games
    .filter(game => game && game.i18n[lang] && game.i18n[lang].tags.includes(tag))
    .map(game => ({
      ...game,
      ...game.i18n[lang] || game.i18n.en,
    }));
}

/**
 * 获取首页游戏数据
 * @param {string} [lang='en'] - 语言代码
 * @param {Object} [options] - 配置选项
 * @param {number} [options.popularLimit=8] - 热门游戏数量限制
 * @param {number} [options.newLimit=8] - 新游戏数量限制
 * @param {number} [options.recommendedLimit=8] - 推荐游戏数量限制
 * @param {string[]} [options.preferredGenres] - 偏好的游戏类别
 * @param {string[]} [options.preferredTags] - 偏好的游戏标签
 * @returns {Promise<Object>} - 返回首页所需的游戏数据
 */
export async function getHomePageGames(lang = 'en', options = {}) {
  const {
    popularLimit = 8,
    newLimit = 8,
    recommendedLimit = 8,
    preferredGenres = [],
    preferredTags = []
  } = options;

  // 获取所有游戏数据
  const slugs = await getAllGameSlugs();
  const games = await Promise.all(slugs.map(slug => loadGameData(slug)));
  
  // 过滤有效游戏并添加国际化数据
  const validGames = games
    .filter(game => game !== null)
    .map(game => getLocalizedGameData(game, lang));

  // 获取热门游戏
  const popularGames = validGames
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, popularLimit);

  // 获取新游戏(按发布日期排序)
  const newGames = validGames
    .filter(game => game.releaseDate) // 确保游戏有发布日期
    .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate))
    .slice(0, newLimit);

  // 智能推荐游戏
  const recommendedGames = validGames
    .map(game => {
      let score = 0;
      
      // 基础分数 - 增加rating权重
      if (game.rating) score += game.rating * 3; // 提高评分的权重
      if (game.plays) score += Math.log10(game.plays);
      
      // 类别匹配加分
      if (preferredGenres.length > 0) {
        score += game.genres.filter(g => 
          preferredGenres.includes(g)
        ).length * 2;
      }
      
      // 标签匹配加分
      if (preferredTags.length > 0) {
        score += game.tags.filter(t => 
          preferredTags.includes(t)
        ).length;
      }
      
      // 新游戏加分
      if (game.isNew) score += 1;
      
      // 热门游戏加分
      if (game.isPopular) score += 1;

      return {
        ...game,
        recommendScore: score
      };
    })
    .sort((a, b) => b.recommendScore - a.recommendScore)
    .slice(0, recommendedLimit);

  // 按类别分组的游戏
  const genresList = await getAllGenres(lang);
  const gamesByGenre = {};
  
  for (const genreObj of genresList) {
    const genreId = genreObj.id;
    gamesByGenre[genreId] = validGames
      .filter(game => game.genres.includes(genreId))
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 8);
  }

  return {
    popularGames,
    newGames,
    recommendedGames,
    gamesByGenre,
    stats: {
      totalGames: validGames.length,
      totalGenres: genresList.length,
      totalTags: (await getAllTags(lang)).length
    }
  };
}

/**
 * 获取随机游戏（辅助函数）
 * @param {number} limit - 返回数量限制
 * @param {Array} excludeIds - 要排除的游戏ID数组
 * @returns {Array} - 返回随机游戏数组
 * @private
 */
function getRandomGames(limit = 4, excludeIds = []) {
  const allGames = getAllGames();
  if (!allGames || allGames.length === 0) return [];
  
  // 排除指定ID的游戏
  const availableGames = allGames.filter(g => !excludeIds.includes(g.id));
  
  // 随机排序并返回指定数量
  return [...availableGames]
    .sort(() => 0.5 - Math.random())
    .slice(0, limit);
}

/**
 * 获取相关游戏
 * @param {string} slug - 当前游戏的slug
 * @param {number} limit - 返回数量限制
 * @param {string} [lang='en'] - 语言代码
 * @returns {Promise<Array>} - 返回相关游戏列表
 */
export async function getRelatedGames(slug, limit = 3, lang = 'en') {
  try {
    const currentGame = await loadGameData(slug);
    if (!currentGame) return [];

    const allSlugs = await getAllGameSlugs();
    const otherGames = await Promise.all(
      allSlugs
        .filter(s => s !== slug)
        .map(s => loadGameData(s))
    );

    // 计算相关性评分
    let relatedGames = otherGames
      .filter(game => game !== null)
      .map(game => {
        const gameI18n = game.i18n[lang] || game.i18n.en;
        const currentGameI18n = currentGame.i18n[lang] || currentGame.i18n.en;
        
        // 计算相关性分数
        let score = 0;
        
        // 确保属性存在
        const gameGenres = gameI18n.genres || [];
        const gameTags = gameI18n.tags || [];
        const currentGenres = currentGameI18n.genres || [];
        const currentTags = currentGameI18n.tags || [];
        
        // 相同类别加3分
        score += gameGenres.filter(g => 
          currentGenres.includes(g)
        ).length * 3;
        
        // 相同标签加1分
        score += gameTags.filter(t => 
          currentTags.includes(t)
        ).length;
        
        return {
          ...game,
          ...gameI18n,
          relevanceScore: score
        };
      })
      .filter(game => game.relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
      
    // 如果相关游戏数量不足，添加随机游戏
    if (relatedGames.length < limit) {
      // 获取已有游戏的ID列表
      const relatedIds = relatedGames.map(g => g.id);
      const excludeIds = [currentGame.id, ...relatedIds];
      
      // 随机选择其他游戏
      const remainingGames = otherGames
        .filter(game => game !== null && !excludeIds.includes(game.id))
        .map(game => ({
          ...game,
          ...game.i18n[lang] || game.i18n.en
        }));
        
      // 随机排序并添加到结果中
      const randomGames = [...remainingGames]
        .sort(() => 0.5 - Math.random())
        .slice(0, limit - relatedGames.length);
        
      relatedGames = [...relatedGames, ...randomGames];
    }
    
    return relatedGames.slice(0, limit);
  } catch (error) {
    return [];
  }
}

/**
 * 清除所有缓存
 */
export function clearAllCache() {
  gameCache.clear();
  gameSlugsCache = null;
  genresCache = null;
  tagsCache = null;
  allGamesCache = null;
}

/**
 * 将游戏标题转换为URL友好的slug格式
 * @param {string} title - 游戏标题
 * @returns {string} - 返回格式化的slug
 */
export function createSlugFromTitle(title) {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * 获取游戏的URL路径
 * @param {Object} game - 游戏对象
 * @returns {string} - 返回游戏详情页的URL路径
 */
export function getGameUrl(game) {
  return `/game/${game.slug}`;
}

/**
 * 检查游戏缓存是否已加载完成
 * @returns {boolean} - 返回缓存是否已加载完成
 */
export function isCacheReady() {
  return allGamesCache !== null && allGamesCache.length > 0;
}

/**
 * 添加缓存状态变更的回调函数
 * @param {Function} callback - 当缓存状态变更时调用的回调函数
 * @returns {Function} - 返回一个函数，调用它可以移除回调
 */
export function onCacheStatusChange(callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }
  
  cacheStatusCallbacks.push(callback);
  
  // 如果缓存已经准备好，立即调用回调
  if (isCacheReady()) {
    try {
      callback(true);
    } catch (error) {
    }
  }
  
  // 返回一个函数，用于取消注册回调
  return () => {
    const index = cacheStatusCallbacks.indexOf(callback);
    if (index !== -1) {
      cacheStatusCallbacks.splice(index, 1);
    }
  };
}

/**
 * 初始化游戏缓存
 * @private
 */
async function initAllGamesCache() {
  try {
    const slugs = await getAllGameSlugs();
    const games = await Promise.all(
      slugs.map(slug => loadGameData(slug))
    );
    
    allGamesCache = games.filter(game => game !== null);
    
    // 通知所有注册的回调函数缓存已准备好
    cacheStatusCallbacks.forEach(callback => {
      try {
        callback(true);
      } catch (error) {
      }
    });
  } catch (error) {
    allGamesCache = [];
    
    // 通知所有注册的回调函数缓存加载失败
    cacheStatusCallbacks.forEach(callback => {
      try {
        callback(false);
      } catch (error) {
      }
    });
  }
}

// 初始化游戏缓存
initAllGamesCache();

/**
 * 根据ID或Slug获取游戏数据（同步版本）
 * @param {string} idOrSlug - 游戏ID或Slug
 * @returns {Object|null} - 返回游戏数据对象，未找到则返回null
 */
export function getGameById(idOrSlug) {
  try {
    // 检查参数是否有效
    if (!idOrSlug) {
      return null;
    }

    // 使用缓存的数据
    if (!allGamesCache || allGamesCache.length === 0) {
      try {
        // 使用 import.meta.glob 替代 require.context
        const gameFiles = import.meta.glob('./data/*.js', { eager: true });
        
        // 提取slug并查找匹配的游戏
        const gamePath = `./data/${idOrSlug}.js`;
        if (gameFiles[gamePath]) {
          return gameFiles[gamePath].default;
        }
      } catch (importError) {
      }
      
      return null;
    }
    
    // 先尝试通过ID查找
    let game = allGamesCache.find(game => game.id === idOrSlug);
    
    // 如果未找到，尝试通过slug查找
    if (!game) {
      game = allGamesCache.find(game => game.slug === idOrSlug);
    }
    
    return game || null;
  } catch (error) {
    return null;
  }
}

/**
 * 获取所有游戏（同步版本）
 * @returns {Array} - 返回游戏数组
 */
export function getAllGames() {
  if (!allGamesCache) {
    return [];
  }
  
  return allGamesCache.map(game => {
    const processedGame = { ...game };
    
    // 确保游戏有thumbnail属性
    if (!processedGame.thumbnail && processedGame.image) {
      processedGame.thumbnail = processedGame.image;
    }
    
    return processedGame;
  });
}

/**
 * 获取热门游戏（同步版本）
 * @param {number} limit - 返回数量限制，默认4个
 * @returns {Array} - 返回热门游戏数组
 */
export function getPopularGames(limit = 4) {
  const games = getAllGames();
  
  // 首先尝试获取被明确标记为热门的游戏
  const popularGames = games.filter(game => game.isPopular);
  
  // 如果有标记为热门的游戏，则返回这些游戏
  if (popularGames.length > 0) {
    return popularGames.slice(0, limit);
  }
  
  // 如果没有标记为热门的游戏，则采用备选策略：
  // 1. 优先选择一些新游戏
  const newGames = games.filter(game => game.isNew).slice(0, Math.ceil(limit/2));
  
  // 2. 再选择一些随机游戏填充剩余位置
  const remainingCount = limit - newGames.length;
  let otherGames = [];
  
  if (remainingCount > 0) {
    // 创建游戏数组的副本并排除已选的新游戏
    const remainingGames = games
      .filter(game => !newGames.some(ng => ng.id === game.id));
    
    // 随机排序并选择需要的数量
    otherGames = [...remainingGames]
      .sort(() => 0.5 - Math.random())
      .slice(0, remainingCount);
  }
  
  // 合并新游戏和随机游戏
  return [...newGames, ...otherGames];
}

/**
 * 获取新游戏（同步版本）
 * @param {number} limit - 返回数量限制，默认4个
 * @returns {Array} - 返回新游戏数组
 */
export function getNewGames(limit = 4) {
  return getAllGames()
    .filter(game => game.isNew)
    .slice(0, limit);
}

/**
 * 获取指定标签的游戏（同步版本）
 * @param {string} tag - 游戏标签
 * @returns {Array} - 返回包含指定标签的游戏数组
 */
export function getGamesByTagSync(tag) {
  return getAllGames().filter(game => game.tags && game.tags.includes(tag));
}

/**
 * 获取所有游戏标签（同步版本）
 * @returns {Array} - 返回不重复的标签数组
 */
export function getAllTagsSync() {
  const allGames = getAllGames();
  const allTags = allGames.flatMap(game => game.tags || []);
  return [...new Set(allTags)]; // 移除重复项
}

/**
 * 获取带游戏数量的标签（同步版本）
 * @returns {Array} - 返回带游戏数量的标签对象数组 {tag, count}
 */
export function getTagsWithCount() {
  const tags = getAllTagsSync();
  return tags.map(tag => ({
    tag,
    count: getGamesByTagSync(tag).length
  }));
}

/**
 * a获取指定类别的游戏（同步版本）
 * @param {string} genre - 游戏类别
 * @returns {Array} - 返回包含指定类别的游戏数组
 */
export function getGamesByGenreSync(genre) {
  return getAllGames().filter(game => game.genres && game.genres.includes(genre));
}

/**
 * 获取所有游戏类别（同步版本）
 * @returns {Array} - 返回不重复的类别数组
 */
export function getAllGenresSync() {
  const allGames = getAllGames();
  const allGenres = allGames.flatMap(game => game.genres || []);
  const uniqueGenres = [...new Set(allGenres)]; // 移除重复项
  
  // 确保.IO类型总是使用.IO格式
  return uniqueGenres.map(genre => {
    if (genre === 'IO' || genre.toLowerCase() === 'io') {
      return '.IO';
    }
    return genre;
  });
}

/**
 * 获取带游戏数量的类别（同步版本）
 * @returns {Array} - 返回带游戏数量的类别对象数组 {genre, count}
 */
export function getGenresWithCount() {
  const genres = getAllGenresSync();
  return genres.map(genre => ({
    genre,
    count: getGamesByGenreSync(genre).length
  }));
}

/**
 * 从已加载的游戏中同步获取相关游戏
 * @param {Object} currentGame - 当前游戏对象
 * @param {number} limit - 获取的游戏数量
 * @param {Object} options - 可选参数
 * @returns {Array} 相关游戏列表
 */
export function getRelatedGamesSync(currentGame, limit = 6, options = {}) {
  const { debug = false } = options;
  const result = [];
  
  // 如果没有当前游戏，返回空数组
  if (!currentGame) {
    return [];
  }
  
  // 获取当前游戏的slug
  const currentSlug = typeof currentGame === 'string' ? currentGame : currentGame.slug;
  
  if (!currentSlug) {
    return [];
  }
  
  // 获取所有游戏（从缓存或直接加载）
  let allGames = [];
  
  // 尝试从缓存获取游戏
  if (allGamesCache && allGamesCache.length > 0) {
    allGames = allGamesCache;
  } else {
    // 如果缓存为空，尝试直接加载游戏
    try {
      const gameFiles = import.meta.glob('./data/*.js', { eager: true });
      allGames = Object.values(gameFiles).map(module => module.default).filter(Boolean);
    } catch (error) {
      return [];
    }
  }
  
  // 简化过滤条件，只排除当前游戏和确保游戏对象有效
  const candidates = allGames.filter(game => 
    game && 
    game.slug && 
    game.slug !== currentSlug
  );
  
  if (candidates.length === 0) {
    if (debug) {
      // 应急策略：如果没有找到任何候选游戏，则绕过slug检查
      const fallbackCandidates = allGames.filter(g => g && g.id !== (currentGame.id || ''));
      
      // 在应急情况下，返回除当前游戏外的随机游戏
      if (fallbackCandidates.length > 0) {
        return shuffleArray(fallbackCandidates).slice(0, limit);
      }
    }
    
    // 最后的备选：直接返回空数组
    return [];
  }
  
  // 获取当前游戏对象
  const gameObj = typeof currentGame === 'string' 
    ? allGames.find(g => g && g.slug === currentGame) 
    : currentGame;
    
  // 提取当前游戏的类型和标签
  if (gameObj) {
    try {
      const currentGenres = gameObj.genres || [];
      const currentTags = gameObj.tags || [];
      
      // 计算每个游戏与当前游戏的相似度并排序
      const scoredGames = candidates.map(game => {
        let score = 0;
        const gameGenres = game.genres || [];
        const gameTags = game.tags || [];
        
        // 相同类型+3分
        for (const genre of currentGenres) {
          if (gameGenres.includes(genre)) {
            score += 3;
          }
        }
        
        // 相同标签+1分
        for (const tag of currentTags) {
          if (gameTags.includes(tag)) {
            score += 1;
          }
        }
        
        return { 
          game,
          score
        };
      });
      
      // 首先添加评分高的游戏
      const sortedGames = scoredGames
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score);
      
      // 添加评分高的游戏
      for (const item of sortedGames) {
        if (result.length >= limit) break;
        result.push(item.game);
      }
    } catch (error) {
      // 出错时不中断，继续添加随机游戏
    }
  }
  
  // 如果相关游戏不足，添加随机游戏
  if (result.length < limit) {
    // 过滤掉已添加的游戏
    const remainingCandidates = candidates.filter(
      game => !result.some(g => g.slug === game.slug)
    );
    
    // 随机选择剩余游戏
    const shuffled = shuffleArray(remainingCandidates);
    const randomGames = shuffled.slice(0, limit - result.length);
    
    result.push(...randomGames);
  }

  // 确保至少返回一些游戏，即使没有相关游戏
  if (result.length === 0 && candidates.length > 0) {
    return shuffleArray(candidates).slice(0, limit);
  }
  
  return result;
}

/**
 * 随机打乱数组
 * @param {Array} array - 要打乱的数组
 * @returns {Array} 打乱后的数组
 */
function shuffleArray(array) {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

// 导出默认对象，包含所有方法
export default {
  // 异步API
  loadGameData,
  getAllGameSlugs,
  getGamesByPage,
  getGamesByGenre,
  getGamesByTag,
  getAllGenres,
  getAllTags,
  getRelatedGames,
  getHomePageGames,
  clearAllCache,
  
  // 同步API
  getGameById,
  getGameUrl,
  createSlugFromTitle,  
  getAllGames,
  getPopularGames,
  getNewGames,
  getGamesByTagSync,
  getGamesByGenreSync,
  getAllTagsSync,
  getAllGenresSync,
  getTagsWithCount,
  getGenresWithCount,
  getRelatedGamesSync,
  
  // 缓存状态API
  isCacheReady,
  onCacheStatusChange
};